import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import { useId } from '../hooks/useId'
import { RadioContext } from './RadioContext'
import type {
  RadioGroupDirection,
  RadioInputColor,
  RadioInputSize,
  StateDefiniton,
} from './RadioContext'

interface RadioGroupProps {
  /**
   * The color scheme of the radio inputs
   */
  color?: RadioInputColor
  /**
   * The layout direction of the radio group
   * @default 'inherit'
   */
  direction?: RadioGroupDirection
  /**
   * The initial selected value.
   * Corresponds to the `value` prop of one of the `Radio.Input` components within the group
   * @default ''
   */
  defaultValue?: string | number
  /**
   * If true, the radio group is disabled
   */
  disabled?: boolean
  /**
   * Whether the radio group is in an error state. Applies aria-invalid to the radiogroup
   * @default false
   */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the radio group
   * @default ''
   */
  helperText?: string
  /**
   * The name attribute for the radio group
   */
  name?: string
  /**
   * Callback fired when the selected value changes
   */
  onChange?: (event: {
    target: { name: string; value: string | number }
  }) => void
  /**
   * The size of the radio inputs
   */
  size?: RadioInputSize
}

function Group({
  children,
  color,
  direction = 'inherit',
  disabled,
  defaultValue = '',
  error = false,
  helperText = '',
  name,
  onChange,
  size,
}: PropsWithChildren<RadioGroupProps>): JSX.Element {
  const id = useId()
  const [state, setState]: [
    StateDefiniton,
    Dispatch<SetStateAction<StateDefiniton>>,
  ] = useState({
    color,
    direction,
    disabled,
    name: name || `coconut-radio-group-${id}`,
    size,
    value: defaultValue,
  })

  useEffect(() => {
    if (disabled !== state.disabled) {
      setState((prevState) => ({ ...prevState, disabled }))
    }
  }, [disabled, state.disabled])

  const setValue = (value: string | number) => {
    setState((prevState) => ({ ...prevState, value }))

    if (onChange) {
      onChange({ target: { name: state.name, value: value } })
    }
  }

  const setLabelledBy = (id: string) =>
    setState((prevState) => ({ ...prevState, labelledBy: id }))

  return (
    <RadioContext.Provider value={[state, setValue, setLabelledBy]}>
      <div className="flex flex-col">
        <div
          aria-invalid={error}
          aria-labelledby={state.labelledBy}
          className={clsx(
            `flex flex-${direction}`,
            direction === 'row' ? 'items-center' : '',
          )}
          role="radiogroup"
        >
          {children}
        </div>
        {helperText ? (
          <span
            aria-live={error ? 'assertive' : 'off'}
            className={clsx(
              'text-xs',
              error ? 'text-error-main' : 'text-black-60',
            )}
          >
            {helperText}
          </span>
        ) : null}
      </div>
    </RadioContext.Provider>
  )
}

export default Group
